<script setup lang="ts">
const CONTACT_TYPES = [
  { value: 'bug', label: 'Bug report', placeholder: 'What went wrong?' },
  { value: 'feature', label: 'Feature suggestion', placeholder: 'What would you like to see?' },
  { value: 'other', label: 'Other', placeholder: "What's on your mind?" },
] as const

const toasts = useToastsStore()
const turnstileSiteKey = useRuntimeConfig().public.turnstileSiteKey

const open = ref(false)
const type = ref<(typeof CONTACT_TYPES)[number]['value']>('bug')
const message = ref('')
const email = ref('')
const includeDiag = ref(false)
const website = ref('') // honeypot - left empty by real users
const submitting = ref(false)
const errorMessage = ref('')

const messagePlaceholder = computed(() => CONTACT_TYPES.find((opt) => opt.value === type.value)?.placeholder)

const turnstileToken = ref('')
const turnstileEl = ref<HTMLElement | null>(null)
let turnstileWidgetId: string | null = null

useBodyScrollLock(open)

function resetForm() {
  type.value = 'bug'
  message.value = ''
  email.value = ''
  includeDiag.value = false
  website.value = ''
  errorMessage.value = ''
  turnstileToken.value = ''
}

function close() {
  open.value = false
  resetForm()
}

async function mountTurnstile() {
  if (!turnstileSiteKey || !turnstileEl.value) return
  if (!(window as any).turnstile) {
    await new Promise<void>((resolve, reject) => {
      const script = document.createElement('script')
      script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js'
      script.async = true
      script.onload = () => resolve()
      script.onerror = () => reject(new Error('Failed to load Turnstile'))
      document.head.appendChild(script)
    }).catch(() => {})
  }
  const turnstile = (window as any).turnstile
  if (!turnstile || !turnstileEl.value) return
  turnstileWidgetId = turnstile.render(turnstileEl.value, {
    sitekey: turnstileSiteKey,
    callback: (token: string) => (turnstileToken.value = token),
    'expired-callback': () => (turnstileToken.value = ''),
  })
}

watch(open, async (isOpen) => {
  if (!isOpen) return
  await nextTick()
  void mountTurnstile()
})

async function submit() {
  if (submitting.value) return
  if (!message.value.trim()) {
    errorMessage.value = 'Please enter a message.'
    return
  }
  submitting.value = true
  errorMessage.value = ''
  try {
    const diag = includeDiag.value ? await buildDiagnosticSummary() : ''
    await $fetch('/api/contact', {
      method: 'POST',
      body: {
        type: type.value,
        message: message.value.trim(),
        email: email.value.trim(),
        diag,
        website: website.value,
        turnstileToken: turnstileToken.value,
      },
    })
    toasts.push({ tone: 'success', message: 'Message sent. Thanks!' })
    close()
  } catch (err: any) {
    errorMessage.value = err?.data?.statusMessage ?? err?.message ?? 'Failed to send. Please try again.'
    if ((window as any).turnstile && turnstileWidgetId) (window as any).turnstile.reset(turnstileWidgetId)
    turnstileToken.value = ''
  } finally {
    submitting.value = false
  }
}
</script>

<template>
  <button
    v-if="!open"
    type="button"
    class="fixed bottom-4 right-4 z-40 rounded-full border border-accent-dim bg-panel px-4 py-2.5 font-mono text-[12px] uppercase tracking-wide text-muted shadow-lg hover:border-accent hover:text-ink"
    @click="open = true"
  >
    Contact
  </button>

  <Transition name="player-modal" appear>
    <div v-if="open" class="route-modal fixed inset-0 z-50 flex items-end justify-end bg-black/60 p-4 backdrop-blur-[2px] sm:items-end sm:justify-end" @click.self="close">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="contact-widget-title"
        class="player-modal-panel flex w-full max-w-sm flex-col rounded-lg border border-border bg-panel p-4 shadow-2xl"
      >
        <div class="mb-3 flex items-baseline justify-between gap-2">
          <span id="contact-widget-title" class="font-display text-sm font-semibold text-ink">Contact</span>
          <button type="button" class="text-muted-dim hover:text-muted" title="Close" @click="close">✕</button>
        </div>

        <div class="mb-3 flex gap-1.5">
          <button
            v-for="opt in CONTACT_TYPES"
            :key="opt.value"
            type="button"
            :aria-pressed="type === opt.value"
            class="flex-1 rounded border px-1.5 py-1.5 font-mono text-[11.5px] uppercase tracking-wide"
            :class="type === opt.value ? 'border-accent bg-accent text-[#1a0f04]' : 'border-border bg-panel-2 text-muted hover:border-accent-dim hover:text-ink'"
            @click="type = opt.value"
          >
            {{ opt.label }}
          </button>
        </div>

        <textarea
          v-model="message"
          rows="5"
          maxlength="5000"
          :placeholder="messagePlaceholder"
          class="mb-2 w-full resize-none rounded border border-border bg-panel-2 p-2 text-xs text-ink placeholder:text-muted-dim focus:border-accent-dim focus:outline-none"
        />

        <input
          v-model="email"
          type="email"
          placeholder="Email (optional, if you'd like a reply)"
          class="mb-2 w-full rounded border border-border bg-panel-2 p-2 text-xs text-ink placeholder:text-muted-dim focus:border-accent-dim focus:outline-none"
        />

        <label class="mb-3 flex items-start gap-2 text-[12px] text-muted-dim">
          <input v-model="includeDiag" type="checkbox" class="mt-0.5" />
          <span>Include diagnostic info (app version, browser, current page - no personal data)</span>
        </label>

        <!-- Honeypot - hidden from real users, visible to naive bots that fill every field. -->
        <input
          v-model="website"
          type="text"
          tabindex="-1"
          autocomplete="off"
          aria-hidden="true"
          class="absolute -left-[9999px] h-0 w-0 opacity-0"
        />

        <div v-if="turnstileSiteKey" ref="turnstileEl" class="mb-3" />

        <div v-if="errorMessage" class="mb-2 text-[12px] text-red-300">{{ errorMessage }}</div>

        <button
          type="button"
          :disabled="submitting"
          class="rounded border border-accent bg-accent px-3 py-1.5 font-mono text-[12px] uppercase tracking-wide text-[#1a0f04] disabled:opacity-50"
          @click="submit"
        >
          {{ submitting ? 'Sending…' : 'Send' }}
        </button>
      </div>
    </div>
  </Transition>
</template>
